import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { FiEdit2, FiPlus, FiSearch, FiTrash2 } from "react-icons/fi";
import api from "../services/api";

const PER_PAGE_OPTIONS = [10, 20, 50, 100];

const SystemSettings = () => {
  const scopeValue = localStorage.getItem("rmc2.scope") || "";
  const scopes = scopeValue
    .split(" ")
    .map((scope) => scope.trim())
    .filter(Boolean);
  const isSystemAdmin = scopes.includes("system_admin");

  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [lastPage, setLastPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [activeRow, setActiveRow] = useState(null);
  const [formData, setFormData] = useState({
    key: "",
    value: "",
    description: "",
  });
  const [formError, setFormError] = useState("");
  const [formSuccess, setFormSuccess] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const totalPages = useMemo(() => {
    return lastPage || Math.max(1, Math.ceil(total / perPage));
  }, [lastPage, total, perPage]);

  useEffect(() => {
    const fetchSettings = async () => {
      setIsLoading(true);
      setLoadError("");
      try {
        const token = localStorage.getItem("rmc2.access_token");
        if (!token) {
          setLoadError("Missing access token.");
          return;
        }

        const response = await api.get("/system-settings", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            search: query,
            page,
            per_page: perPage,
          },
        });

        if (response.data?.status !== "success") {
          setLoadError("Failed to load system settings.");
          return;
        }

        const data = response.data?.data || {};
        setRows(data.rows || []);
        setTotal(data.total || 0);
        setLastPage(data.last_page || 1);
      } catch (error) {
        setLoadError("Failed to load system settings.");
      } finally {
        setIsLoading(false);
      }
    };

    if (isSystemAdmin) {
      fetchSettings();
    }
  }, [query, page, perPage, refreshKey, isSystemAdmin]);

  if (!isSystemAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setPage(1);
    setQuery(search.trim());
  };

  const handleOpenCreate = () => {
    setFormData({
      key: "",
      value: "",
      description: "",
    });
    setFormError("");
    setFormSuccess("");
    setIsCreateOpen(true);
  };

  const handleOpenEdit = (row) => {
    setActiveRow(row);
    setFormData({
      key: row.key || "",
      value: row.value || "",
      description: row.description || "",
    });
    setFormError("");
    setFormSuccess("");
    setIsEditOpen(true);
  };

  const handleCloseModal = () => {
    setIsCreateOpen(false);
    setIsEditOpen(false);
    setActiveRow(null);
    setIsSaving(false);
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setIsSaving(true);
    setFormError("");
    setFormSuccess("");

    if (!formData.key.trim() || !formData.value.trim()) {
      setFormError("Key and value are required.");
      setIsSaving(false);
      return;
    }

    try {
      const token = localStorage.getItem("rmc2.access_token");
      if (!token) {
        setFormError("Missing access token.");
        return;
      }

      if (isCreateOpen) {
        const response = await api.post(
          "/system-settings",
          {
            key: formData.key.trim(),
            value: formData.value.trim(),
            description: formData.description.trim() || null,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (response.data?.status !== "success") {
          const message = response.data?.message || "Failed to create setting.";
          if (message && typeof message === "object") {
            setFormError(Object.values(message).flat().join(" "));
          } else {
            setFormError(message);
          }
          return;
        }

        setFormSuccess("Setting created.");
        setIsCreateOpen(false);
      }

      if (isEditOpen && activeRow?.id) {
        const response = await api.put(
          `/system-settings/${activeRow.id}`,
          {
            key: formData.key.trim(),
            value: formData.value.trim(),
            description: formData.description.trim() || null,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (response.data?.status !== "success") {
          const message = response.data?.message || "Failed to update setting.";
          if (message && typeof message === "object") {
            setFormError(Object.values(message).flat().join(" "));
          } else {
            setFormError(message);
          }
          return;
        }

        setFormSuccess("Setting updated.");
        setIsEditOpen(false);
      }

      setPage(1);
      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      setFormError("Failed to save setting.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (row) => {
    const confirmDelete = window.confirm(`Delete setting "${row.key}"?`);
    if (!confirmDelete) {
      return;
    }

    try {
      const token = localStorage.getItem("rmc2.access_token");
      if (!token) {
        setLoadError("Missing access token.");
        return;
      }

      const response = await api.delete(`/system-settings/${row.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data?.status !== "success") {
        setLoadError(response.data?.message || "Failed to delete setting.");
        return;
      }

      setPage(1);
      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      setLoadError("Failed to delete setting.");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 rounded-3xl border border-slate-200 bg-white px-6 py-6 shadow-sm lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
            System Admin
          </p>
          <h1 className="mt-2 text-2xl font-semibold text-slate-900">
            System Settings
          </h1>
          <p className="mt-2 text-sm text-slate-500">
            Manage platform configuration values, announcements, and runtime flags.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <form onSubmit={handleSearchSubmit} className="relative w-full sm:w-64">
            <FiSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              type="text"
              placeholder="Search settings"
              className="w-full rounded-2xl border border-slate-200 bg-white py-2 pl-10 pr-4 text-sm text-slate-700 shadow-sm focus:border-[var(--brand-orange)] focus:outline-none"
            />
          </form>
          <button
            type="button"
            onClick={handleSearchSubmit}
            className="rounded-2xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800"
          >
            Search
          </button>
          <button
            type="button"
            onClick={handleOpenCreate}
            className="inline-flex items-center gap-2 rounded-2xl bg-[var(--brand-orange)] px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-orange-200 transition hover:bg-[var(--brand-orange-dark)]"
          >
            <FiPlus />
            New Setting
          </button>
        </div>
      </div>

      {loadError && (
        <div className="rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-600">
          {loadError}
        </div>
      )}

      <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-6 py-4 text-sm text-slate-500">
          <span>
            {isLoading ? "Loading settings..." : `${total} setting(s)`}
          </span>
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
              Rows
            </span>
            <select
              value={perPage}
              onChange={(event) => {
                setPerPage(Number(event.target.value));
                setPage(1);
              }}
              className="rounded-xl border border-slate-200 bg-white px-3 py-1 text-sm text-slate-700 shadow-sm focus:border-[var(--brand-orange)] focus:outline-none"
            >
              {PER_PAGE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-[0.2em] text-slate-400">
              <tr>
                <th className="px-6 py-4 text-left">Key</th>
                <th className="px-6 py-4 text-left">Value</th>
                <th className="px-6 py-4 text-left">Description</th>
                <th className="px-6 py-4 text-left">Updated</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {rows.map((row) => (
                <tr key={row.id} className="hover:bg-slate-50/80">
                  <td className="px-6 py-4 font-semibold text-slate-900">
                    {row.key}
                  </td>
                  <td className="px-6 py-4 text-slate-600">
                    <div className="max-w-[320px] truncate">{row.value}</div>
                  </td>
                  <td className="px-6 py-4 text-slate-600">
                    <div className="max-w-[320px] truncate">
                      {row.description || "-"}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-slate-500">
                    {row.updated_at
                      ? new Date(row.updated_at).toLocaleString()
                      : "-"}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => handleOpenEdit(row)}
                        className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 transition hover:border-slate-300 hover:text-slate-800"
                      >
                        <FiEdit2 />
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(row)}
                        className="inline-flex items-center gap-1 rounded-full border border-rose-200 px-3 py-1 text-xs font-semibold text-rose-600 transition hover:border-rose-300 hover:text-rose-700"
                      >
                        <FiTrash2 />
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {!isLoading && rows.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-slate-500">
                    No settings found.
                  </td>
                </tr>
              )}
              {isLoading && (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-slate-500">
                    Loading settings...
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 text-sm text-slate-500">
          <span>
            Page {page} of {totalPages}
          </span>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 transition hover:border-slate-300 hover:text-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Previous
            </button>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
              className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 transition hover:border-slate-300 hover:text-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {(isCreateOpen || isEditOpen) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-6 py-8">
          <div className="w-full max-w-xl rounded-3xl bg-white p-6 shadow-2xl shadow-slate-900/30">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                  System Settings
                </p>
                <h3 className="mt-2 text-2xl font-semibold text-slate-900">
                  {isCreateOpen ? "Create Setting" : "Edit Setting"}
                </h3>
              </div>
              <button
                type="button"
                onClick={handleCloseModal}
                className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-500 hover:border-slate-300 hover:text-slate-700"
              >
                Close
              </button>
            </div>

            {formError && (
              <div className="mt-4 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-600">
                {formError}
              </div>
            )}
            {formSuccess && (
              <div className="mt-4 rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm text-emerald-600">
                {formSuccess}
              </div>
            )}

            <form onSubmit={handleSave} className="mt-6 space-y-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                  Key
                </label>
                <input
                  value={formData.key}
                  onChange={(event) =>
                    setFormData((prev) => ({ ...prev, key: event.target.value }))
                  }
                  type="text"
                  className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700 shadow-sm focus:border-[var(--brand-orange)] focus:outline-none"
                />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                  Value
                </label>
                <textarea
                  value={formData.value}
                  onChange={(event) =>
                    setFormData((prev) => ({ ...prev, value: event.target.value }))
                  }
                  rows={4}
                  className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700 shadow-sm focus:border-[var(--brand-orange)] focus:outline-none"
                />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                  Description
                </label>
                <input
                  value={formData.description}
                  onChange={(event) =>
                    setFormData((prev) => ({
                      ...prev,
                      description: event.target.value,
                    }))
                  }
                  type="text"
                  className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700 shadow-sm focus:border-[var(--brand-orange)] focus:outline-none"
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="rounded-2xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="rounded-2xl bg-[var(--brand-orange)] px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-orange-200 transition hover:bg-[var(--brand-orange-dark)]"
                >
                  {isSaving ? "Saving..." : "Save Setting"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemSettings;
